<template>
  <div
    v-if="!hideIfNoPages || (hideIfNoPages && lastPage > 1)"
    :class="{ 'mt-4': shouldApplyMarginTop }"
  >
    <nav class="d-flex justify-content-center align-items-center">
      <div
        class="nav-item arrow"
        :class="`${current <= 1 ? 'disabled' : 'active'}`"
        @click.prevent="prevPage()"
      >
        &lt; &nbsp; {{ previous }}
      </div>
      <div
        v-if="shouldShowFirst3Dot || minVal > 1"
        :id="0"
        :key="page_selector_first_page"
        class="nav-item"
        :class="`${1 === current || current === (-1) && n === 0 ? 'active' : ''}`"
        @click.prevent="current = 1"
      >
        {{ 1 }}
      </div>
      <div v-if="shouldShowFirst3Dot || minVal > 1">
        ...
      </div>
      <div
        v-for="n in pages"
        :id="`${n}`"
        :key="`page_selector_${n}`"
        class="nav-item"
        :class="`${n === current || current === (-1) && n === 0 ? 'active' : 'not-active'}`"
        @click.prevent="current = n"
      >
        {{ n }}
      </div>
      <div v-if="shouldShowLast3Dot">
        ...
      </div>
      <div
        v-if="shouldShowLast3Dot"
        :id="lastPage - 1"
        :key="page_selector_last_page"
        class="nav-item"
        :class="`${lastPage === current || current === (-1) && n === 0 ? 'active' : ''}`"
        @click.prevent="current = lastPage"
      >
        {{ lastPage }}
      </div>
      <div
        class="nav-item arrow"
        :class="`${current >= lastPage ? 'disabled' : 'active'}`"
        @click.prevent="nextPage()"
      >
        {{ next }} &nbsp; &gt;
      </div>
    </nav>

    <pre
      v-if="debug"
      class="border"
    >
    Count {{ count }}
    pages {{ pages }}
    current {{ current }}
    pageSize {{ pageSize }}
    value {{ value }}
    lastPage {{ lastPage }}
</pre>
  </div>
</template>

<script>
import _ from 'lodash';

const MAX_PAGE_SIZE = 7;
const OFFSET_FROM_CENTER = 2;

export default {
  name: 'BasePagination',
  props: {
    debug: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Number, String],
      default: 0,
    },
    pagesToShow: {
      type: Number,
      default: 8,
    },
    pageSize: {
      type: Number,
      default: 25,
    },
    count: {
      type: Number,
      required: true,
    },
    next: {
      type: String,
      required: false,
      default: 'Next', // Right label text
    },
    previous: {
      type: String,
      required: false,
      default: 'Prev', // Left label text
    },
    hideIfNoPages: {
      type: Boolean,
      default: true,
    },
    firstThreeDotDisplayOffset: {
      type: Number,
      default: 6,
    },
    lastThreeDotDisplayOffset: {
      type: Number,
      default: 6,
    },
    shouldApplyMarginTop: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      current: 1,
    };
  },
  computed: {
    minVal() {
      const { pagesToShow } = this;

      let min = this.current - MAX_PAGE_SIZE;

      if (min >= this.lastPage - pagesToShow) {
        min = this.lastPage - pagesToShow;
      }

      if (min <= 0) {
        min = 0;
      }

      if (this.shouldShowFirst3Dot) {
        min += 1;
      }

      if (this.shouldShowFirst3Dot && !this.shouldShowLast3Dot) {
        const awayFromEnd = this.lastPage - this.current;
        let offset = MAX_PAGE_SIZE - awayFromEnd;

        if (offset < 0) {
          offset = 0;
        }

        min = this.current - offset;

        if (min < 1) {
          min = 1; // as shouldShowFirst3Dot is already true which means min must be greater than 0
        }
      }

      const zerothIndexOffset = 1;
      min += zerothIndexOffset;

      if (this.shouldShowFirst3Dot && this.shouldShowLast3Dot) {
        min = this.current - OFFSET_FROM_CENTER;
      }

      return min;
    },

    maxVal() {
      let max;
      const { pagesToShow } = this;
      const min = this.minVal;

      max = min + MAX_PAGE_SIZE;

      if (max < pagesToShow) {
        max = pagesToShow;
      }
      if (max > this.lastPage) {
        max = this.lastPage;
      }

      if (this.shouldShowLast3Dot) {
        max -= 1;
      }

      const lastIndexOffsetLodash = 1;

      if (this.shouldShowFirst3Dot && this.shouldShowLast3Dot) {
        max = this.current + OFFSET_FROM_CENTER;
      }

      max += lastIndexOffsetLodash;

      return max;
    },

    pages() {
      return _.range(this.minVal, this.maxVal);
    },
    lastPage() {
      return Math.ceil(this.count / this.pageSize);
    },
    shouldShowFirst3Dot() {
      return (this.current) >= this.firstThreeDotDisplayOffset;
    },
    shouldShowLast3Dot() {
      return this.current <= (this.lastPage - this.lastThreeDotDisplayOffset);
    },
  },
  watch: {
    value: {
      handler() {
        this.current = Number(this.value);
      },
      immediate: true,
    },
    current() {
      this.$emit('pageChange', this.current);
      this.$emit('input', this.current);
    },
  },
  methods: {
    prevPage() {
      this.current -= 1;
      if (this.current < 1) {
        this.current = 1;
      }
    },
    nextPage() {
      this.current += 1;
      if (this.current > this.pages) {
        this.current = this.pages;
      }
    },
  },
};
</script>
<style lang="scss">
.nav-item {
  border-radius: 3px;
  transition: background-color .3s ease;
  padding: 3px 8px;
  cursor: pointer;
  color: $dark-black;
  font-size: 12px;
  font-family: $font-family-sans-serif-light;
  opacity: 0.85;

  &.disabled {
    color: $gray-light-2;
    cursor: unset;
    pointer-events: none;
  }
  &.not-active {
    color: $gray-light-2;
  }
  &.active {
    color: $dark-black;
    font-family: $font-family-sans-serif-regular;
    opacity: 1;
  }
  &:hover {
    background-color: $gray-light;
  }
  &.arrow {
    font-size: 12px;
    padding: 3px 18px;
    font-family: $font-family-sans-serif-light;
  }
}
</style>
